import { gql } from "@apollo/client";

export const ResourceHubBaseFragment = gql`
  fragment ResourceHubBaseFragment on Resource {
    documentId
    title
    slug
    briefDescription
    subHeader
    publicationDate
    resourceStatus
    expireResourceStatusAt
    assetsCount
    type {
      documentId
      type
    }
    category {
      documentId
      category
      color
    }
    cover {
      url
      alternativeText
      mime
    }
  }
`;

export const ResourceHubDefaultContentTemplateFragment = gql`
  fragment ResourceHubDefaultContentTemplateFragment on ResourceDefaultContentTemplateDynamicZone {
    __typename
    ... on ComponentCommonH3 {
      id
      text
    }
    ... on ComponentCommonTitle {
      id
      title
    }
    ... on ComponentCommonAuthorDetailed {
      id
      authors: authors_connection {
        nodes {
          documentId
          name
          role
          bio
          avatar {
            url
            alternativeText
          }
        }
      }
    }
    ... on ComponentCommonAuthor {
      id
      publicationDate
      author {
        name
        role
      }
    }
    ... on ComponentCommonDivider {
      id
    }
    ... on ComponentCommonEmbedded {
      id
      link
      provider
    }
    ... on ComponentCommonTextEditor {
      id
      markdownText
    }
    ... on ComponentCommonQuote {
      id
      quote
    }
    ... on ComponentCommonFaqComponent {
      id
      faq {
        title
        description
      }
    }
    ... on ComponentCommonTags {
      id
    }
    ... on ComponentCommonRelatedContent {
      id
    }
    ... on ComponentCommonDownloadableContentList {
      id
      list {
        id
        title
        description
        content {
          url
          name
          ext
          mime
          size
        }
      }
    }
    ... on ComponentCommonAudio {
      id
      transcription
      audio {
        url
        name
      }
    }
    ... on ComponentResourceAssets {
      id
      sectionTitle
      assets: assets_connection {
        nodes {
          documentId
          title
          description
          publishedAt
          updatedAt
          previewStatus
          scanStatus
          content {
            url
            name
            ext
            mime
            size
          }
          resourceAssetHistory: resourceAssetHistory_connection {
            nodes {
              version
            }
          }
        }
      }
    }
    ... on ComponentResourceResourcesRelated {
      id
      resources: resources_connection {
        nodes {
          documentId
          title
          slug
          briefDescription
          cover {
            url
            alternativeText
          }
          type {
            type
          }
        }
      }
    }
  }
`;

export const QUERY_FETCH_RESOURCE_HUB_LIST = gql`
  query fetchResourceHubList(
    $page: Int!
    $pageSize: Int
    $title: String
    $categoryDocumentIds: [ID]
    $schoolContextDocumentId: ID!
    $userDocumentId: ID!
  ) {
    resources: resources_connection(
      pagination: { page: $page, pageSize: $pageSize }
      sort: "publicationDate:desc"
      filters: {
        publishedAt: { notNull: true }
        title: { containsi: $title }
        category: { documentId: { in: $categoryDocumentIds } }
        or: [
          { restrictedSharing: { eq: false } }
          { restrictedSharing: { null: true } }
          {
            restrictedSharing: { eq: true }
            or: [
              { schools: { documentId: { in: [$schoolContextDocumentId] } } }
              { staff: { documentId: { eq: $userDocumentId } } }
            ]
          }
        ]
      }
    ) {
      nodes {
        ...ResourceHubBaseFragment
      }
      pageInfo {
        page
        pageCount
        total
        pageSize
      }
    }
  }
  ${ResourceHubBaseFragment}
`;

export const QUERY_FETCH_RESOURCE_HUB_BY_SLUG = gql`
  query fetchResourceHubBySlug($slug: String!) {
    resources_connection(
      filters: { slug: { eq: $slug } }
      pagination: { limit: 1 }
    ) {
      nodes {
        documentId
        title
        slug
        briefDescription
        subHeader
        publicationDate
        resourceStatus
        expireResourceStatusAt
        type {
          documentId
          type
        }
        category {
          documentId
          category
        }
        cover {
          url
          alternativeText
          mime
        }
        resourceHistory {
          documentId
          version
          createdAt
        }
        DefaultContentTemplate {
          ...ResourceHubDefaultContentTemplateFragment
        }
      }
    }
  }
  ${ResourceHubDefaultContentTemplateFragment}
`;

export const QUERY_FETCH_RESOURCE_HUB_CATEGORIES = gql`
  query fetchResourceHubCategories {
    resourceCategories {
      documentId
      category
      color
      subCategories: subCategories_connection {
        nodes {
          documentId
          category
          color
          subCategories: subCategories_connection {
            nodes {
              documentId
              category
              color
              subCategories: subCategories_connection {
                nodes {
                  documentId
                  category
                  color
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const QUERY_FETCH_RESOURCE_HUB_TYPES = gql`
  query fetchResourceHubTypes {
    resourceTypes {
      documentId
      type
    }
  }
`;

export const QUERY_FETCH_RESOURCE_HUB_RESOURCES_BY_CATEGORY = gql`
  query fetchResourceHubResourcesByCategory(
    $categoryDocumentIds: [ID!]!
    $limit: Int
    $schoolContextDocumentId: ID!
    $userDocumentId: ID!
  ) {
    resources: resources_connection(
      pagination: { page: 1, pageSize: $limit }
      sort: "publicationDate:desc"
      filters: {
        publishedAt: { notNull: true }
        category: { documentId: { in: $categoryDocumentIds } }
        or: [
          { restrictedSharing: { eq: false } }
          { restrictedSharing: { null: true } }
          {
            restrictedSharing: { eq: true }
            or: [
              { schools: { documentId: { in: [$schoolContextDocumentId] } } }
              { staff: { documentId: { eq: $userDocumentId } } }
            ]
          }
        ]
      }
    ) {
      nodes {
        ...ResourceHubBaseFragment
      }
    }
  }
  ${ResourceHubBaseFragment}
`;

export const QUERY_FETCH_RESOURCE_HUB_CATEGORY_IDS_WITH_RESOURCES = gql`
  query fetchResourceHubCategoryIdsWithResources(
    $schoolContextDocumentId: ID!
    $userDocumentId: ID!
  ) {
    resources: resources_connection(
      pagination: { limit: -1 }
      filters: {
        publishedAt: { notNull: true }
        or: [
          { restrictedSharing: { eq: false } }
          { restrictedSharing: { null: true } }
          {
            restrictedSharing: { eq: true }
            or: [
              { schools: { documentId: { in: [$schoolContextDocumentId] } } }
              { staff: { documentId: { eq: $userDocumentId } } }
            ]
          }
        ]
      }
    ) {
      nodes {
        category {
          documentId
        }
      }
    }
  }
`;

export const QUERY_FETCH_RESOURCE_HUB_PAGE = gql`
  query fetchResourceHubPage {
    resourceHubPage {
      title
      description
      cardLinkText
      loadMoreText
      noResultsText
      resultsTitle
      howToUseThisTitle
      downloadButtonText
      previewButtonText
      downloadAllButtonText
    }
  }
`;

export const QUERY_HOME_RESOURCE_SECTION = gql`
  query homeResourceSection(
    $categoryDocumentIds: [ID]!
    $schoolContextDocumentId: ID!
    $userDocumentId: ID!
  ) {
    resources: resources_connection(
      pagination: { page: 1, pageSize: 2 }
      sort: "publicationDate:desc"
      filters: {
        publishedAt: { notNull: true }
        category: { documentId: { in: $categoryDocumentIds } }
        or: [
          { restrictedSharing: { eq: false } }
          { restrictedSharing: { null: true } }
          {
            restrictedSharing: { eq: true }
            or: [
              { schools: { documentId: { in: [$schoolContextDocumentId] } } }
              { staff: { documentId: { eq: $userDocumentId } } }
            ]
          }
        ]
      }
    ) {
      nodes {
        ...ResourceHubBaseFragment
      }
    }
  }
  ${ResourceHubBaseFragment}
`;
